import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useContext,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { isEqual } from 'lodash'
import { useLocation } from 'react-router-dom'

import EventDetailsContext from '../../../../context/EventDetails/EventDetails'
import { useInventory } from '../InventoryProvider/InventoryProvider'

import {
  scalingInventoryToEvent,
  assignSeatmapToEvent,
  applyTemplateToEvent,
  resetMapState,
  createTicketGroups,
  createSeatmapTemplate,
} from '../../../../utilities/api'

import {
  fullHeightContainer,
  removeFullHeightContainer,
} from '../../../../utilities/helpers'

import { Navigation } from './Navigation'
import { ResetMapModal } from './ResetMapModal'
import { ResetConfigurationModal } from './ResetConfigurationModal'
import { MapConfigurations } from './MapConfigurations'
import { SelectMapModal } from '../SelectMapModal'
import { SaveModal } from './SaveModal'
import { UnsavedChangesModal } from './UnsavedChangesModal'
import { PageLoadingContainer } from '../../../PageLoadingContainer'
import {
  SeatmapTutorial,
  SEATMAP_TUTORIAL_STORAGE_KEY,
} from './SeatmapTutorial'
import { printScalingSeatmap } from './printScalingSeatmap'
import {
  omitBlockedSeats,
  collectHeldAndKilledSeatIds,
  collectInventorySeatIds,
  inventoryHasBeenSaved,
  removedInventorySpotIds,
  seatKeptOffOpen,
  stripHeldAndKilledSeatsFromOffers,
  unavailableTicketSeatIds,
} from './openInventorySeats'
import {
  normalizeHiddenSectionIds,
  sameHiddenSectionIds,
  toggleHiddenSectionId,
} from '../../../../utilities/hiddenSections'

const isOpenInventoryOffer = (offer) => {
  if (!offer) return false
  if ((offer.inventoryType || '').toLowerCase() === 'exclusive') {
    return false
  }

  const inventoryType = (offer.inventoryType || '').toLowerCase()
  const name = (offer.name || '').toLowerCase()

  return (
    inventoryType === 'open' ||
    name === 'open' ||
    name === 'standard admission' ||
    offer.isDefaultOffer === true
  )
}

const resolveOfferSeatData = (inventoryOffer, stateOffer, dbOffer) => {
  const pickSeats = (primary, fallback) =>
    Array.isArray(primary) ? primary : (fallback ?? [])

  if (inventoryOffer) {
    return {
      seats: pickSeats(
        inventoryOffer.seats,
        stateOffer?.seats ?? dbOffer?.seats
      ),
      gaSeats:
        inventoryOffer.gaSeats != null
          ? JSON.parse(JSON.stringify(inventoryOffer.gaSeats))
          : (stateOffer?.gaSeats ?? dbOffer?.gaSeats ?? {}),
    }
  }

  return {
    seats: pickSeats(stateOffer?.seats, dbOffer?.seats),
    gaSeats: stateOffer?.gaSeats ?? dbOffer?.gaSeats ?? {},
  }
}

const saveErrorMessage = (error) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  'Something went wrong while saving. Please try again.'

const findMatchingOfferKey = (offersMap, inventoryKey, inventoryOffer) => {
  return Object.keys(offersMap).find(
    (key) =>
      String(key) === String(inventoryKey) ||
      String(offersMap[key]?.id) === String(inventoryOffer?.id) ||
      (offersMap[key]?.name || '').toLowerCase() ===
        (inventoryOffer?.name || '').toLowerCase()
  )
}

export default function ConfigureMap({
  eventId,
  offerId,
  event,
  data,
  background,
  seatmaps,
  templates = [],
  activeTab,
  setActiveTab,
  selectedMapId,
  configuration,
  isReloading,
  setIsReloading,
  reloadData,
}) {
  const navigate = useNavigate()
  const location = useLocation()

  const {
    isEventOnsale,
    isEventPublished,
    hasEventEnded,
    isEventSoldout,
    setStandardAdmissionOfferHasInventory,
  } = useContext(EventDetailsContext)

  const {
    setHolds,
    setKills,
    holds,
    kills,
    offers,
    setOffers,
    priceLevels,
    setPriceLevels,
    unassignedSeats,
    setUnassignedSeats,
    sold,
    setSold,
    setResale,
    SOLDCOLOR,
    RESALECOLOR,
    UNASSIGNED_COLOR,
    openScalingIncompleteModal,
    clearSelectedSeats,
  } = useInventory()

  // initial price levels to check if anything has changed when saving
  const [initialPriceLevels, setInitialPriceLevels] = useState({})

  // initial inventory to check if anything has changed when saving
  const [initialInventory, setInitialInventory] = useState({})

  const [initialSelectedId, setInitialSelectedId] = useState()
  // local state of selectedMapId in case of reverting back to original value
  const [selectedId, setSelectedId] = useState(null)
  // array of all seats
  const [totalSeats, setTotalSeats] = useState({})

  const [showResetConfiguration, setShowResetConfiguration] = useState(false)

  const [showReset, setShowReset] = useState(false)

  const [choice, setChoice] = useState(null)

  const [show, setShow] = useState(false)

  const [showSave, setShowSave] = useState(false)

  const [templateName, setTemplateName] = useState('')

  const [saveStep, setSaveStep] = useState(1)

  const [isSaving, setIsSaving] = useState(false)

  const [isConfigSaved, setIsConfigSaved] = useState(false)

  const [loadedState, setLoadedState] = useState(false)

  const [isMapDisabled, setIsMapDisabled] = useState(false)

  const [showSeatmapTutorial, setShowSeatmapTutorial] = useState(false)

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)

  const [hiddenSectionIds, setHiddenSectionIds] = useState(() =>
    normalizeHiddenSectionIds(event?.inventory?.hiddenSections)
  )
  const [initialHiddenSectionIds, setInitialHiddenSectionIds] = useState(() =>
    normalizeHiddenSectionIds(event?.inventory?.hiddenSections)
  )

  // set html and body to full height
  useLayoutEffect(() => {
    ;['html', 'body'].forEach((el) =>
      document.querySelector(el).classList.add('full-height')
    )

    return () => {
      ;['html', 'body'].forEach((el) =>
        document.querySelector(el).classList.remove('full-height')
      )
    }
  }, [location.pathname])

  useEffect(() => {
    if (isReloading || !data) return undefined
    try {
      if (!localStorage.getItem(SEATMAP_TUTORIAL_STORAGE_KEY)) {
        const timer = setTimeout(() => setShowSeatmapTutorial(true), 900)
        return () => clearTimeout(timer)
      }
    } catch (_) {
      /* localStorage unavailable */
    }
    return undefined
  }, [isReloading, data])

  const markSeatmapTutorialComplete = () => {
    try {
      localStorage.setItem(SEATMAP_TUTORIAL_STORAGE_KEY, '1')
    } catch (_) {
      /* localStorage unavailable */
    }
  }

  useLayoutEffect(() => {
    // hide navbar
    const nav = document.getElementById('navbar')
    nav.style.display = 'none'
    // hide event banner
    const banner = document.getElementById('banner')
    banner.style.display = 'none'
    // hide sidebar
    const sidebarNav = document.getElementById('sidebarMenu')
    sidebarNav.style.display = 'none'

    // modify sidebar container
    const container = document.getElementById('main-container')
    container.classList.add('sidebar-container-lg')
    container.classList.add('seatmap-layout')
    fullHeightContainer(container)

    // modify container
    const content = document.getElementById('main-content')
    content.classList.add('pb-0')
    content.classList.add('full-height-wrapper')

    return () => {
      nav.style.display = ''
      banner.style.display = ''
      sidebarNav.style.display = ''
      container.classList.remove('sidebar-container-lg')
      container.classList.remove('seatmap-layout')
      removeFullHeightContainer(container)
      content.classList.remove('pb-0')
      content.classList.remove('full-height-wrapper')
    }
  }, [])

  useEffect(() => {
    if (offerId) setActiveTab('inventory')
  }, [offerId])

  // set to selected map id
  useEffect(() => {
    const selectedId = selectedMapId || null
    setSelectedId(selectedId)
    setInitialSelectedId(selectedId)
  }, [selectedMapId])

  //   useEffect(() => {
  //     // Set up an interval to update state every 3 seconds
  //     const intervalId = setInterval(() => {
  //       setIsConfigSaved(false);
  //     }, 3000); // 3000 milliseconds = 3 seconds

  //     // Clear the interval when the component is unmounted
  //     return () => clearInterval(intervalId);
  //   }, []);

  // controls if map can be changed
  useEffect(() => {
    setIsMapDisabled(hasEventEnded)
  }, [hasEventEnded])

  const getUnassignedSeats = (GASeatsBySection, regularSeats, priceLevels) => {
    const allPriceLevelSeats = Object.values(priceLevels).reduce(
      (acc, priceLevel) => {
        const { objectGASeatsBySection, regularSeats: plRegularSeats } =
          getObjectAllSeats(priceLevel)

        // Merge regular seats
        acc.regularSeats.push(...plRegularSeats)

        // Merge GA seats by section
        Object.entries(objectGASeatsBySection).forEach(([sectionId, seats]) => {
          if (!acc.GASeatsBySection[sectionId]) {
            acc.GASeatsBySection[sectionId] = []
          }
          acc.GASeatsBySection[sectionId].push(...seats)
        })

        return acc
      },
      { GASeatsBySection: {}, regularSeats: [] }
    )

    // Find unassigned regular seats
    const unassignedRegularSeats = regularSeats.filter(
      (seat) => !allPriceLevelSeats.regularSeats.includes(seat)
    )

    // Find unassigned GA seats
    const unassignedGASeatsBySection = Object.entries(GASeatsBySection).reduce(
      (acc, [sectionId, seats]) => {
        const unassignedSeats = seats.filter(
          (seat) =>
            !allPriceLevelSeats.GASeatsBySection[sectionId]?.includes(seat)
        )
        if (unassignedSeats.length > 0) {
          acc[sectionId] = unassignedSeats
        }
        return acc
      },
      {}
    )
    return {
      UnassignedGASeatsBySection: unassignedGASeatsBySection,
      UnassignedRegularSeats: unassignedRegularSeats,
    }
  }

  const getAllSeats = (mapping) => {
    const GASeatsBySection = {}
    const regularSeats = []

    // Iterate through sections
    for (const [sectionId, section] of Object.entries(mapping.sections)) {
      if (!section.zoomable && section.spots) {
        GASeatsBySection[sectionId] = section.spots
      }
    }

    // Add all seats to regularSeats
    if (mapping.seats) {
      regularSeats.push(...Object.keys(mapping.seats))
    }

    return { GASeatsBySection, regularSeats }
  }

  // PL's / inventories no longer have just one seats array
  // we also have GASeatsBySection
  // so this returns all seats
  const getObjectAllSeats = (object) => {
    const objectGASeatsBySection = {}
    const regularSeats = object?.seats ?? []

    if (object?.gaSeats) {
      for (const [sectionId, seats] of Object.entries(object?.gaSeats)) {
        if (seats) {
          objectGASeatsBySection[sectionId] = seats
        }
      }
    }

    return { objectGASeatsBySection, regularSeats }
  }

  const getObjectTotalSeatCount = (object) => {
    if (!object) return 0
    const { objectGASeatsBySection, regularSeats } = getObjectAllSeats(object)

    // Count regular seats
    const regularSeatCount = regularSeats.length

    // Count GA seats
    const gaSeatCount = Object.values(objectGASeatsBySection).reduce(
      (total, sectionSeats) => total + sectionSeats.length,
      0
    )

    return regularSeatCount + gaSeatCount
  }

  // object for all scaling/inventory
  const getDefaultObj = (
    id,
    name,
    color,
    regularSeats,
    GASeatsBySection,
    inventoryType,
    disabled
  ) => {
    const updatedSeats = regularSeats ?? []
    const updatedGASeats = GASeatsBySection ?? {}
    return {
      id,
      name,
      color,
      seats: updatedSeats,
      gaSeats: updatedGASeats,
      inventoryType,
      disabled,
    }
  }

  const setUnassignedSeatsUtility = (GASeatsBySection, regularSeats) => {
    const unassignedSeatsObj = {
      unassigned: {
        ...getDefaultObj(
          'unassigned',
          'Unassigned',
          UNASSIGNED_COLOR,
          regularSeats,
          GASeatsBySection,
          undefined,
          false // disabled
        ),
      },
    }

    setUnassignedSeats(unassignedSeatsObj)
  }

  const getTicketSeatsForStatuses = (event, statuses) => {
    const matchingTickets = event?.tickets?.filter((ticket) =>
      statuses.has(ticket?.on_sale_status)
    )
    const gaSpotIds = new Set()
    const collectGaSpots = (offer) => {
      Object.values(offer?.gaSeats || {}).forEach((seats) => {
        ;(Array.isArray(seats) ? seats : []).forEach((id) => gaSpotIds.add(id))
      })
      ;(offer?.subOffers || []).forEach(collectGaSpots)
    }
    Object.values(event?.inventory?.offers || {}).forEach(collectGaSpots)

    const isGaTicket = (ticket) =>
      ticket?.generalAdmission === true || gaSpotIds.has(ticket?.seatId)

    const regularSeats = matchingTickets
      ?.filter((ticket) => ticket?.seatId && !isGaTicket(ticket))
      ?.map((ticket) => ticket.seatId)

    // GA spots leave Open only when that spot's ticket is unavailable.
    // A spot moved back from a hold stays for sale.
    const gaSeats = matchingTickets
      ?.filter((ticket) => ticket?.seatId && isGaTicket(ticket))
      ?.reduce((acc, ticket) => {
        const sectionId = ticket.sectionId || '_unsectioned'
        if (!acc[sectionId]) acc[sectionId] = []
        acc[sectionId].push(ticket.seatId)
        return acc
      }, {})

    return {
      regularSeats: regularSeats || [],
      gaSeats: gaSeats || {},
    }
  }

  // Include package reserved seats so season inventory shows on the Inventory tab.
  const getSoldSeats = (event) => {
    const { regularSeats, gaSeats } = getTicketSeatsForStatuses(
      event,
      new Set([
        'sold',
        'reserved',
        'reservedForGroupPurchase',
        'pendingTransfer',
      ])
    )
    return { soldRegularSeats: regularSeats, soldGASeats: gaSeats }
  }

  const getResaleSeats = (event) => {
    const { regularSeats, gaSeats } = getTicketSeatsForStatuses(
      event,
      new Set(['resaleAvailable'])
    )
    return { resaleRegularSeats: regularSeats, resaleGASeats: gaSeats }
  }

  const setSoldSeatsUtility = (event) => {
    const { soldRegularSeats, soldGASeats } = getSoldSeats(event)
    const soldSeatsObj = {
      1: {
        ...getDefaultObj(
          1,
          'Sold',
          SOLDCOLOR,
          soldRegularSeats,
          soldGASeats,
          undefined,
          true
        ),
      },
    }

    setSold(soldSeatsObj)
    return { soldRegularSeats, soldGASeats }
  }

  const setResaleSeatsUtility = (event) => {
    const { resaleRegularSeats, resaleGASeats } = getResaleSeats(event)
    setResale({
      1: {
        ...getDefaultObj(
          1,
          'Resale',
          RESALECOLOR,
          resaleRegularSeats,
          resaleGASeats,
          undefined,
          true
        ),
      },
    })
    return { resaleRegularSeats, resaleGASeats }
  }

  const mergeGASeats = (...seatMaps) => {
    const merged = {}
    seatMaps.forEach((seatMap) => {
      Object.entries(seatMap || {}).forEach(([sectionId, seats]) => {
        if (!merged[sectionId]) merged[sectionId] = []
        merged[sectionId].push(...(seats || []))
      })
    })
    return merged
  }

  const filterOutSoldSeats = (offer, soldRegularSeats, soldGASeats) => {
    const soldGaIds = new Set(
      Object.values(soldGASeats || {}).flatMap((seats) => seats || [])
    )
    const soldIds = new Set([...(soldRegularSeats || []), ...soldGaIds])
    const filteredSeats = offer.seats
      ? offer.seats.filter((seatId) => !soldIds.has(seatId))
      : []

    const filteredGASeats = {}
    if (offer.gaSeats) {
      Object.entries(offer.gaSeats).forEach(([sectionId, seats]) => {
        filteredGASeats[sectionId] = (seats || []).filter(
          (seatId) => !soldIds.has(seatId)
        )
      })
    }

    return {
      ...offer,
      seats: filteredSeats,
      gaSeats: filteredGASeats,
    }
  }

  // Normalize holds/kills from saved template/event so each item and category
  // has seats and gaSeats (avoids crashes when restoring from DB)
  const normalizeHoldsOrKills = (items) => {
    if (!items || typeof items !== 'object') return {}
    return Object.fromEntries(
      Object.entries(items).map(([key, item]) => {
        if (!item) return [key, item]
        const normalizedCategories = item.categories
          ? Object.fromEntries(
              Object.entries(item.categories).map(([catKey, cat]) => [
                catKey,
                {
                  ...cat,
                  seats: Array.isArray(cat?.seats) ? cat.seats : [],
                  gaSeats:
                    cat?.gaSeats && typeof cat.gaSeats === 'object'
                      ? cat.gaSeats
                      : {},
                },
              ])
            )
          : {}
        return [
          key,
          {
            ...item,
            seats: Array.isArray(item?.seats) ? item.seats : [],
            gaSeats:
              item?.gaSeats && typeof item.gaSeats === 'object'
                ? item.gaSeats
                : {},
            categories: normalizedCategories,
          },
        ]
      })
    )
  }

  // Stored mapState
  const setMapStateFromEvent = (
    pricingLevels,
    inventory,
    GASeatsBySection,
    regularSeats,
    sourceEvent = event
  ) => {
    if (pricingLevels) setPriceLevels(pricingLevels)
    if (inventory?.kills) setKills(normalizeHoldsOrKills(inventory.kills))
    if (inventory?.holds) setHolds(normalizeHoldsOrKills(inventory.holds))
    const { soldRegularSeats, soldGASeats } = setSoldSeatsUtility(sourceEvent)
    const { resaleRegularSeats, resaleGASeats } =
      setResaleSeatsUtility(sourceEvent)
    const unavailableRegularSeats = [...soldRegularSeats, ...resaleRegularSeats]
    const unavailableGASeats = mergeGASeats(soldGASeats, resaleGASeats)

    // Use event.offers (DB relation) for seat data, with fallback to
    // event.inventory.offers (JSON) when offers are empty or lack seat data
    // (e.g. after applying a template, before offers are fully populated)
    const dbOffers = sourceEvent?.offers || []
    const inventoryOffers = inventory?.offers || {}
    const openOffersFromDb = dbOffers.filter(isOpenInventoryOffer)
    const openOfferFromDb =
      openOffersFromDb.find((offer) => offer.isDefaultOffer) ||
      openOffersFromDb[0] ||
      dbOffers[0]
    const openOfferFromInventory =
      Object.values(inventoryOffers).find(isOpenInventoryOffer)

    if (dbOffers.length > 0 || openOfferFromInventory) {
      const standardOffer = openOfferFromDb || openOfferFromInventory
      const id = standardOffer?.id || 1
      const { seats, gaSeats } = resolveOfferSeatData(
        openOfferFromInventory,
        null,
        openOfferFromDb
      )
      const filteredStandardOffer = filterOutSoldSeats(
        { ...standardOffer, seats, gaSeats },
        unavailableRegularSeats,
        unavailableGASeats
      )
      const openWithoutHolds = omitBlockedSeats(
        filteredStandardOffer?.seats,
        filteredStandardOffer?.gaSeats,
        collectHeldAndKilledSeatIds(inventory?.holds, inventory?.kills)
      )

      const processedOffers = {
        [id]: {
          ...getDefaultObj(
            id,
            'Open',
            '#3ea9f7',
            openWithoutHolds.seats,
            openWithoutHolds.gaSeats,
            'open',
            false
          ),
          subOffers: [],
        },
      }

      const addOpenSubOffer = (offer) => {
        if (!offer?.id || String(offer.id) === String(id)) return
        const alreadyAdded = processedOffers[id].subOffers.some(
          (subOffer) => String(subOffer.id) === String(offer.id)
        )
        if (alreadyAdded) return
        const filteredOffer = filterOutSoldSeats(
          offer,
          unavailableRegularSeats,
          unavailableGASeats
        )
        processedOffers[id].subOffers.push({
          ...filteredOffer,
          color: '#3ea9f7',
          inventoryType: 'open',
        })
      }

      // Restore secondary open offers saved on inventory, then merge DB offers.
      ;(openOfferFromInventory?.subOffers || []).forEach(addOpenSubOffer)
      openOffersFromDb
        .filter((offer) => String(offer.id) !== String(id))
        .forEach(addOpenSubOffer)

      // Merge exclusive offers from DB and stored inventory.
      const exclusiveInventoryOffers = Object.entries(inventoryOffers).filter(
        ([, invOffer]) => !isOpenInventoryOffer(invOffer)
      )
      const exclusiveDbOffers = dbOffers.filter(
        (offer) => !isOpenInventoryOffer(offer)
      )
      const exclusiveSources = [
        ...exclusiveDbOffers.map((offer) => ({
          key: String(offer.id),
          dbOffer: offer,
          invOffer:
            inventoryOffers[offer.id] || inventoryOffers[String(offer.id)],
        })),
        ...exclusiveInventoryOffers
          .filter(
            ([key, invOffer]) =>
              !exclusiveDbOffers.some(
                (dbOffer) =>
                  String(dbOffer.id) === String(key) ||
                  String(dbOffer.id) === String(invOffer.id) ||
                  (dbOffer.name || '').toLowerCase() ===
                    (invOffer.name || '').toLowerCase()
              )
          )
          .map(([key, invOffer]) => ({
            key,
            dbOffer: null,
            invOffer,
          })),
      ]

      exclusiveSources.forEach(({ key, dbOffer, invOffer }) => {
        const offerId = dbOffer?.id ?? invOffer?.id ?? key
        const { seats: exclusiveSeats, gaSeats: exclusiveGaSeats } =
          resolveOfferSeatData(invOffer, null, dbOffer)
        const mergedOffer = {
          ...(dbOffer || {}),
          ...invOffer,
          id: offerId,
          seats: exclusiveSeats,
          gaSeats: exclusiveGaSeats,
          inventoryType:
            invOffer?.inventoryType || dbOffer?.inventoryType || 'exclusive',
          color: invOffer?.color || dbOffer?.color,
          name: invOffer?.name || dbOffer?.name,
        }
        const filteredOffer = filterOutSoldSeats(
          mergedOffer,
          unavailableRegularSeats,
          unavailableGASeats
        )
        processedOffers[offerId] = {
          ...filteredOffer,
          ...omitBlockedSeats(
            filteredOffer?.seats,
            filteredOffer?.gaSeats,
            collectHeldAndKilledSeatIds(inventory?.holds, inventory?.kills)
          ),
        }
      })

      setOffers(processedOffers)
    } else if (pricingLevels && Object.keys(pricingLevels).length > 0) {
      // Has pricing levels but no offer data - default all seats to Open
      setDefaultMapState(GASeatsBySection, regularSeats, sourceEvent)
    }

    const { UnassignedGASeatsBySection, UnassignedRegularSeats } =
      getUnassignedSeats(GASeatsBySection, regularSeats, pricingLevels)

    setUnassignedSeatsUtility(
      UnassignedGASeatsBySection,
      UnassignedRegularSeats
    )
  }

  // Default state
  const setDefaultMapState = (
    GASeatsBySection,
    regularSeats,
    sourceEvent = event
  ) => {
    setUnassignedSeatsUtility(GASeatsBySection, regularSeats)
    const { soldRegularSeats, soldGASeats } = setSoldSeatsUtility(sourceEvent)
    const { resaleRegularSeats, resaleGASeats } =
      setResaleSeatsUtility(sourceEvent)
    const openSeats = filterOutSoldSeats(
      { seats: regularSeats, gaSeats: GASeatsBySection },
      [...soldRegularSeats, ...resaleRegularSeats],
      mergeGASeats(soldGASeats, resaleGASeats)
    )
    const openOffers = (sourceEvent?.offers || []).filter(isOpenInventoryOffer)
    const standardOffer =
      openOffers.find((offer) => offer.isDefaultOffer) || openOffers[0]
    const id = standardOffer?.id || 1
    const openDefaultOffer = {
      ...getDefaultObj(
        id,
        'Open',
        '#3ea9f7',
        openSeats.seats,
        openSeats.gaSeats,
        'open',
        false
      ),
      subOffers: openOffers
        .filter((offer) => String(offer.id) !== String(id))
        .map((offer) => ({
          ...offer,
          color: offer.color || '#3ea9f7',
          inventoryType: 'open',
          seats: openSeats.seats,
          gaSeats: openSeats.gaSeats,
        })),
    }
    setOffers({ [id]: openDefaultOffer })
  }

  // Boot-load initial state for map, either stored or manual
  useEffect(() => {
    if (!event || loadedState) return
    setLoadedState(true) // so we never do this more than once

    const { pricingLevels, inventory, seatmap } = event
    const { GASeatsBySection, regularSeats } = getAllSeats(seatmap.mapping)

    if (pricingLevels) {
      setMapStateFromEvent(
        pricingLevels,
        inventory,
        GASeatsBySection,
        regularSeats
      )
    } else {
      // AllSeats are same as unassignedSeats
      setDefaultMapState(GASeatsBySection, regularSeats)
    }

    const hidden = normalizeHiddenSectionIds(inventory?.hiddenSections)
    setHiddenSectionIds(hidden)
    setInitialHiddenSectionIds(hidden)

    setTotalSeats({ gaSeats: GASeatsBySection, seats: regularSeats })
  }, [])

  // set initial state to check if anything has changed when saving
  useEffect(() => {
    // set initial price levels and initial inventory only if empty (on initial load)
    if (
      initialPriceLevels &&
      initialInventory &&
      Object.keys(initialPriceLevels)?.length === 0 &&
      Object.keys(initialInventory)?.length === 0
    ) {
      // has holds or kills or offers
      if (
        priceLevels &&
        holds &&
        kills &&
        offers &&
        (Object.keys(priceLevels)?.length > 0 ||
          Object.keys(holds)?.length > 0 ||
          Object.keys(kills)?.length > 0 ||
          Object.keys(offers)?.length > 0)
      ) {
        setInitialPriceLevels(priceLevels)
        setInitialInventory({
          offers,
          holds,
          kills,
        })
      }
    }
  }, [priceLevels, offers, holds, kills])

  // Replace both the working list and the saved baseline. Leaving the previous
  // ids in place makes the next save write them onto inventory that dropped them.
  const replaceHiddenSections = (ids) => {
    const hidden = normalizeHiddenSectionIds(ids)
    setHiddenSectionIds(hidden)
    setInitialHiddenSectionIds(hidden)
  }

  const resetConfiguration = async (eventWithNewConfig) => {
    setPriceLevels({})
    setHolds({})
    setKills({})
    setInitialInventory({})
    setInitialPriceLevels({})

    // change tab to scaling
    setActiveTab('scaling')

    let gaSeats = totalSeats.gaSeats
    let seats = totalSeats.seats
    let event = eventWithNewConfig

    const { GASeatsBySection, regularSeats } = getAllSeats(
      event?.seatmap?.mapping
    )
    gaSeats = GASeatsBySection
    seats = regularSeats

    // reset offers and unassigned seats to totalSeats
    setUnassignedSeatsUtility(gaSeats, seats)
    const generalOnsale = (event?.offers || []).find(
      (offer) => offer?.isDefaultOffer
    )
    const id =
      generalOnsale?.id || Object.values(event?.offers || {})[0]?.id || 1

    const openDefaultOffer = getDefaultObj(
      id,
      'Open',
      '#3ea9f7',
      seats,
      gaSeats,
      'open',
      false
    )
    setOffers({ [id]: openDefaultOffer })
    replaceHiddenSections(event?.inventory?.hiddenSections)
  }

  // Rebuild map state from an event that already carries its new configuration
  // (applying a template writes pricing levels, holds, kills and offers server
  // side, so the screen has to read them back rather than start from blank).
  const loadConfigurationFromEvent = (eventWithNewConfig) => {
    setInitialInventory({})
    setInitialPriceLevels({})
    setActiveTab('scaling')

    const { GASeatsBySection, regularSeats } = getAllSeats(
      eventWithNewConfig?.seatmap?.mapping
    )
    setTotalSeats({ gaSeats: GASeatsBySection, seats: regularSeats })

    const { pricingLevels, inventory } = eventWithNewConfig || {}

    setPriceLevels({})
    setHolds({})
    setKills({})

    if (pricingLevels && Object.keys(pricingLevels).length > 0) {
      setMapStateFromEvent(
        pricingLevels,
        inventory,
        GASeatsBySection,
        regularSeats,
        eventWithNewConfig
      )
    } else {
      setDefaultMapState(GASeatsBySection, regularSeats, eventWithNewConfig)
    }

    replaceHiddenSections(eventWithNewConfig?.inventory?.hiddenSections)
  }

  // reset configuration modal
  const handleShowResetConfiguration = () => setShowResetConfiguration(true)

  const handleCloseResetConfiguration = (_, choice) => {
    setShowResetConfiguration(false)
    // show select map modal
    if (choice) setShow(true)
  }

  // reset map configuration modal - reset or select map
  const handleClick = (e, choice) => {
    if (choice) {
      setChoice(choice)
      // close reset configuration modal and show select map modal
      handleCloseResetConfiguration(e, choice)
    } else {
      setShowResetConfiguration(false)
      // reset map
      setShowReset(true)
    }
  }

  // reset seatmap: reset configurations and reload event
  const resetMap = (eventUUID, handler) => {
    return new Promise((resolve, reject) => {
      let data = {
        eventUUID,
      }
      // reset - undo changes made to seatmap
      resetMapState(data)
        .then(() => {
          // reload event and map data - only clear spinner after full success
          return reloadData().then((res) => {
            resetConfiguration(res?.data) // reset data in localStorage and inventories
            setIsSaving(false)
            handler()
            resolve()
          })
        })
        .catch((err) => {
          reject(err)
          setIsSaving(false)
        })
    })
  }

  // reset map modal
  const handleReset = () => {
    setIsSaving(true)
    resetMap(event?.uuid, handleCloseReset).catch((err) => {
      setIsSaving(false)
    })
  }

  const handleCloseReset = () => setShowReset(false)

  const handleSubmit = (isExisting) => {
    const isTemplate = isExisting ?? choice === 2
    if (!selectedId) return

    setIsSaving(true)
    const numericId = Number(selectedId)

    if (isTemplate) {
      if (Number.isNaN(numericId) || numericId <= 0) {
        console.error('Invalid template selection')
        setIsSaving(false)
        return
      }

      applyTemplateToEvent({
        eventUUID: event.uuid,
        templateId: numericId,
      })
        .then(() => reloadData())
        .then((res) => {
          loadConfigurationFromEvent(res?.data)
          setIsSaving(false)
          handleClose()
        })
        .catch((err) => {
          console.error(err)
          setIsSaving(false)
        })
      return
    }

    const data = {
      eventUUID: event.uuid,
      seatMapID: numericId,
    }
    // add selected seatmap to event
    assignSeatmapToEvent(data)
      .then(() => {
        resetMap(event?.uuid, handleClose)
          .then()
          .catch((err) => {
            console.error('Error resetting map:', err)
          })
      })
      .catch((err) => {
        console.error(err)
        setIsSaving(false)
      })
  }

  const handleCancel = () => {
    setSelectedId(initialSelectedId)
    handleClose()
  }

  // select map modal
  const handleClose = () => {
    setChoice(null)
    setShow(false)
  }

  // save modal - opens popup to name and save template
  const handleShowSave = () => {
    setTemplateName('')
    setSaveStep(2)
    setShowSave(true)
  }

  const handleCloseSave = () => {
    setShowSave(false)
    setTemplateName('')
    setSaveStep(1)
  }

  const handlePrint = () => {
    clearSelectedSeats()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        printScalingSeatmap({
          event,
          configuration,
          priceLevels,
          unassignedSeats,
          holds,
          kills,
          getObjectTotalSeatCount,
          background,
        })
      })
    })
  }

  const handleSaveTemplate = (e) => {
    e?.preventDefault()
    if (!templateName?.trim()) return
    if (!event?.venue?.uuid) {
      console.error('Cannot save template: venue not found')
      return
    }
    setIsSaving(true)
    createSeatmapTemplate({
      eventUUID: eventId,
      venueUUID: event.venue.uuid,
      name: templateName.trim(),
      pricingLevels: getPriceLevels(),
      inventory: getTemplateInventory(),
      seatmap: configuration?.id,
    })
      .then(() => {
        setIsSaving(false)
        handleCloseSave()
        setIsConfigSaved(true)
      })
      .catch((err) => {
        console.error('Failed to save template:', err)
        setIsSaving(false)
      })
  }

  // get price levels from inventory provider; not sessions
  const getPriceLevels = () => {
    let pricingLevels = priceLevels
    return pricingLevels
  }

  // get inventory (offers, holds, kills) for template or saving
  const getInventory = () => buildInventoryForSave()

  const filterSoldSeatsFromInventoryOffers = (offersMap) => {
    const { soldRegularSeats, soldGASeats } = getSoldSeats(event)
    const { resaleRegularSeats, resaleGASeats } = getResaleSeats(event)
    return Object.fromEntries(
      Object.entries(offersMap || {}).map(([key, offer]) => {
        const filteredOffer = filterOutSoldSeats(
          offer,
          [...soldRegularSeats, ...resaleRegularSeats],
          mergeGASeats(soldGASeats, resaleGASeats)
        )
        if (filteredOffer?.subOffers?.length) {
          filteredOffer.subOffers = filteredOffer.subOffers.map((subOffer) =>
            filterOutSoldSeats(
              subOffer,
              [...soldRegularSeats, ...resaleRegularSeats],
              mergeGASeats(soldGASeats, resaleGASeats)
            )
          )
        }
        return [key, filteredOffer]
      })
    )
  }

  const getTemplateInventory = () => {
    const offersCopy = JSON.parse(JSON.stringify(offers || {}))
    const inventoryOffers = event?.inventory?.offers || {}

    // Add exclusive offers that exist in stored inventory but not local state.
    Object.entries(inventoryOffers).forEach(([key, invOffer]) => {
      if (isOpenInventoryOffer(invOffer)) return
      const stateKey = findMatchingOfferKey(offersCopy, key, invOffer)
      if (stateKey) return

      const { seats, gaSeats } = resolveOfferSeatData(invOffer, null, null)
      const targetKey = String(invOffer.id ?? key)
      offersCopy[targetKey] = {
        ...invOffer,
        id: invOffer.id ?? key,
        seats,
        gaSeats,
      }
    })

    const openOfferKey = Object.keys(offersCopy).find((key) =>
      isOpenInventoryOffer(offersCopy[key])
    )

    if (openOfferKey) {
      syncOpenOfferSubOffers(offersCopy, openOfferKey)
    }

    return {
      offers: filterSoldSeatsFromInventoryOffers(offersCopy),
      holds,
      kills,
      hiddenSections: normalizeHiddenSectionIds(hiddenSectionIds),
    }
  }

  const syncOpenOfferSubOffers = (offersCopy, openOfferKey) => {
    const openOffer = offersCopy[openOfferKey]
    if (!openOffer) return

    const openOffersFromEvent = (event?.offers || []).filter(
      isOpenInventoryOffer
    )
    const subOffersById = new Map()

    ;(openOffer.subOffers || []).forEach((subOffer) => {
      if (subOffer?.id != null) {
        subOffersById.set(String(subOffer.id), { ...subOffer })
      }
    })

    openOffersFromEvent.forEach((dbOffer) => {
      if (String(dbOffer.id) === String(openOffer.id)) return
      const subOfferKey = String(dbOffer.id)
      if (!subOffersById.has(subOfferKey)) {
        subOffersById.set(subOfferKey, {
          id: dbOffer.id,
          name: dbOffer.name,
          color: dbOffer.color || '#3ea9f7',
          inventoryType: 'open',
          disabled: false,
        })
      }
    })

    const sharedSeats = Array.isArray(openOffer.seats) ? openOffer.seats : []
    const sharedGaSeats =
      openOffer.gaSeats && typeof openOffer.gaSeats === 'object'
        ? openOffer.gaSeats
        : {}

    openOffer.subOffers = Array.from(subOffersById.values()).map(
      (subOffer) => ({
        ...subOffer,
        inventoryType: 'open',
        color: subOffer.color || '#3ea9f7',
        seats: [...sharedSeats],
        gaSeats: JSON.parse(JSON.stringify(sharedGaSeats)),
      })
    )
    offersCopy[openOfferKey] = openOffer
  }

  const buildInventoryForSave = () => {
    const offersCopy = stripHeldAndKilledSeatsFromOffers(
      JSON.parse(JSON.stringify(offers || {})),
      holds,
      kills
    )
    const openOfferKey = Object.keys(offersCopy).find((key) =>
      isOpenInventoryOffer(offersCopy[key])
    )

    if (!openOfferKey) {
      return {
        offers: offersCopy,
        holds,
        kills,
        hiddenSections: normalizeHiddenSectionIds(hiddenSectionIds),
      }
    }

    const openOffer = offersCopy[openOfferKey] || {}
    openOffer.seats = Array.isArray(openOffer.seats) ? openOffer.seats : []
    openOffer.gaSeats =
      openOffer.gaSeats && typeof openOffer.gaSeats === 'object'
        ? openOffer.gaSeats
        : {}

    const assignedSeats = new Set()
    const assignedGASeatsBySection = {}

    const registerOfferSeats = (offerObj) => {
      if (!offerObj) return
      ;(offerObj.seats || []).forEach((seatId) => assignedSeats.add(seatId))

      Object.entries(offerObj.gaSeats || {}).forEach(([sectionId, seats]) => {
        if (!assignedGASeatsBySection[sectionId]) {
          assignedGASeatsBySection[sectionId] = new Set()
        }
        ;(seats || []).forEach((seatId) =>
          assignedGASeatsBySection[sectionId].add(seatId)
        )
      })
    }

    Object.values(offersCopy).forEach((offerObj) => {
      registerOfferSeats(offerObj)
      ;(offerObj?.subOffers || []).forEach(registerOfferSeats)
    })

    const soldSeatIds = collectInventorySeatIds(Object.values(sold || {}))
    unavailableTicketSeatIds(event?.tickets).forEach((id) =>
      soldSeatIds.add(id)
    )
    const removedSpotIds = removedInventorySpotIds(
      event?.inventory?.removedSpots
    )
    const heldOrKilled = collectHeldAndKilledSeatIds(holds, kills)
    const blockedIds = new Set([
      ...soldSeatIds,
      ...removedSpotIds,
      ...collectInventorySeatIds([
        ...Object.values(holds || {}),
        ...Object.values(kills || {}),
      ]),
    ])
    const savedInventory = event?.inventory
    const inventoryWasSaved = inventoryHasBeenSaved(savedInventory)
    const savedPriceLevelIds = collectInventorySeatIds(
      Object.values(event?.pricingLevels || {})
    )
    const savedInventoryIds = collectInventorySeatIds([
      ...Object.values(savedInventory?.offers || {}),
      ...Object.values(savedInventory?.holds || {}),
      ...Object.values(savedInventory?.kills || {}),
      ...(event?.offers || []),
    ])
    const keepOffOpen = (seatId) =>
      seatKeptOffOpen(seatId, {
        blockedIds,
        savedPriceLevelIds,
        savedInventoryIds,
        inventoryWasSaved,
      })

    const openWithoutBlockedSeats = omitBlockedSeats(
      openOffer.seats,
      openOffer.gaSeats,
      { ...heldOrKilled, ids: blockedIds }
    )
    // Drop spots the price level still lists after a sale or assignment
    // already took them off the saved offer. Holding the rest must not
    // publish those spots again.
    openOffer.seats = openWithoutBlockedSeats.seats.filter(
      (seatId) => !keepOffOpen(seatId)
    )
    openOffer.gaSeats = Object.fromEntries(
      Object.entries(openWithoutBlockedSeats.gaSeats).map(
        ([sectionId, seats]) => [
          sectionId,
          (seats || []).filter((seatId) => !keepOffOpen(seatId)),
        ]
      )
    )

    Object.values(priceLevels || {}).forEach((level) => {
      ;(level?.seats || []).forEach((seatId) => {
        if (assignedSeats.has(seatId) || keepOffOpen(seatId)) return
        openOffer.seats.push(seatId)
        assignedSeats.add(seatId)
      })

      Object.entries(level?.gaSeats || {}).forEach(([sectionId, seats]) => {
        if (!openOffer.gaSeats[sectionId]) {
          openOffer.gaSeats[sectionId] = []
        }
        if (!assignedGASeatsBySection[sectionId]) {
          assignedGASeatsBySection[sectionId] = new Set()
        }

        ;(seats || []).forEach((seatId) => {
          if (
            assignedGASeatsBySection[sectionId].has(seatId) ||
            keepOffOpen(seatId)
          ) {
            return
          }
          openOffer.gaSeats[sectionId].push(seatId)
          assignedGASeatsBySection[sectionId].add(seatId)
        })
      })
    })

    openOffer.seats = [...new Set(openOffer.seats)]
    openOffer.gaSeats = Object.fromEntries(
      Object.entries(openOffer.gaSeats).map(([sectionId, seats]) => [
        sectionId,
        [...new Set(seats || [])],
      ])
    )
    offersCopy[openOfferKey] = openOffer
    syncOpenOfferSubOffers(offersCopy, openOfferKey)

    return {
      offers: offersCopy,
      holds,
      kills,
      hiddenSections: normalizeHiddenSectionIds(hiddenSectionIds),
    }
  }

  // Saves price levels/inventory to offers
  // assigns to Standard Admission offer
  const handleSaveToOffer = () => {
    const inventoryForSave = buildInventoryForSave()
    const offerData = {
      eventUUID: eventId,
      pricingLevels: getPriceLevels(),
      inventory: inventoryForSave,
    }
    return scalingInventoryToEvent(offerData)
      .then(() => createTicketGroups({ eventUUID: eventId }))
      .then(() => inventoryForSave)
  }

  const persistMapChanges = () => {
    setIsSaving(true)
    return handleSaveToOffer()
      .then((inventoryForSave) => {
        const savedOffers = stripHeldAndKilledSeatsFromOffers(
          inventoryForSave?.offers,
          inventoryForSave?.holds,
          inventoryForSave?.kills
        )
        setOffers(savedOffers)
        setStandardAdmissionOfferHasInventory(true)
        setInitialPriceLevels(priceLevels)
        setInitialInventory({
          offers: savedOffers,
          holds: inventoryForSave?.holds,
          kills: inventoryForSave?.kills,
        })
        setInitialHiddenSectionIds(
          normalizeHiddenSectionIds(inventoryForSave?.hiddenSections)
        )
        setIsConfigSaved(true)
      })
      .catch((error) => {
        console.error('Failed to save offer:', error)
        const isScalingIncomplete = !areAllSeatsAssignedToPriceLevels()
        openScalingIncompleteModal(
          isScalingIncomplete ? null : saveErrorMessage(error)
        )
        throw error
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleSave = () => {
    if (isMapDisabled || !hasUnsavedChanges()) return
    persistMapChanges()
  }

  const areAllSeatsAssignedToPriceLevels = () => {
    // all seats assigned to price levels
    const priceLevels = getPriceLevels()
    const priceLevelSeatCount = Object.values(priceLevels).reduce(
      (total, level) => {
        return total + getObjectTotalSeatCount(level)
      },
      0
    )

    // get all seats from seatmap
    let seats = []

    seats.push({ seats: Object.keys(event?.seatmap?.mapping?.seats ?? {}) })

    // get GA "seats"
    Object.values(event?.seatmap?.mapping?.sections ?? {}).forEach(
      (section) => {
        if (section?.spots) {
          seats.push({ seats: section?.spots })
        }
      }
    )

    const totalSeats = seats?.flatMap((arr) => arr.seats)
    return priceLevelSeatCount === totalSeats?.length
  }

  const hasUnsavedChanges = () =>
    !(
      isEqual(initialPriceLevels ?? {}, priceLevels ?? {}) &&
      isEqual(initialInventory?.offers ?? {}, offers ?? {}) &&
      isEqual(initialInventory?.holds ?? {}, holds ?? {}) &&
      isEqual(initialInventory?.kills ?? {}, kills ?? {}) &&
      sameHiddenSectionIds(initialHiddenSectionIds, hiddenSectionIds)
    )

  const toggleHiddenSection = (sectionId) => {
    if (isMapDisabled) return
    setHiddenSectionIds((current) => toggleHiddenSectionId(current, sectionId))
  }

  const navigateAfterLeave = () => {
    navigate('..')
  }

  const saveAndLeave = () => {
    setIsReloading(true)
    return persistMapChanges()
      .then(() => {
        navigate(`/event/${eventId}/offers`)
      })
      .finally(() => {
        setIsReloading(false)
      })
  }

  // when leaving seat map
  const handleLeave = () => {
    if (!hasUnsavedChanges()) {
      navigateAfterLeave()
      return
    }

    setShowUnsavedChangesModal(true)
  }

  const handleCloseUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(false)
  }

  const handleContinueWithoutSaving = () => {
    setShowUnsavedChangesModal(false)
    navigateAfterLeave()
  }

  const handleSaveFromUnsavedModal = () => {
    setShowUnsavedChangesModal(false)
    saveAndLeave()
  }

  const eventSeatLabels = useMemo(() => {
    const fromTickets = Object.fromEntries(
      (event?.tickets || [])
        .filter((ticket) => ticket?.seatId && ticket?.additionalLabel)
        .map((ticket) => [ticket.seatId, ticket.additionalLabel])
    )
    return {
      ...fromTickets,
      ...(event?.inventory?.seatLabels || {}),
    }
  }, [event?.tickets, event?.inventory?.seatLabels])

  return (
    <>
      <div data-testid="configure-map-section">
        {isReloading ? (
          <PageLoadingContainer style="without-sidebar" size="sm" />
        ) : (
          <>
            <Navigation
              event={event}
              isEventPublished={isEventPublished}
              hasEventEnded={hasEventEnded}
              isSaved={isConfigSaved}
              configuration={configuration}
              templateName={''}
              unassignedSeats={Object.values(unassignedSeats)[0]}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleShowResetConfiguration={handleShowResetConfiguration}
              handleShowSave={handleShowSave}
              handlePrint={handlePrint}
              handleLeave={handleLeave}
              handleSave={handleSave}
              isSaving={isSaving}
              isMapDisabled={isMapDisabled}
              hasUnsavedChanges={hasUnsavedChanges()}
              onStartTutorial={() => setShowSeatmapTutorial(true)}
            />
            <MapConfigurations
              isEventOnsale={isEventOnsale}
              allowResales={event?.organization?.setting?.allowResales === true}
              data={data}
              activeTab={activeTab}
              isMapDisabled={isMapDisabled}
              priceLevels={priceLevels}
              setPriceLevels={setPriceLevels}
              unassignedSeats={unassignedSeats}
              setUnassignedSeats={setUnassignedSeats}
              setOffers={setOffers}
              getDefaultObj={getDefaultObj}
              getObjectTotalSeatCount={getObjectTotalSeatCount}
              background={background}
              venueSlug={
                event?.venue?.slug || event?.venueSlug || event?.venue_slug
              }
              venueName={
                event?.venue?.name || event?.venueName || event?.venue_name
              }
              seatIdentityById={event?.seatIdentityById}
              eventUuid={event?.uuid}
              eventTimezone={event?.timezone}
              eventSeatLabels={eventSeatLabels}
              hiddenSectionIds={hiddenSectionIds}
              onToggleSectionHidden={toggleHiddenSection}
            />
          </>
        )}
      </div>

      <ResetConfigurationModal
        show={showResetConfiguration}
        templates={templates}
        handleClose={handleCloseResetConfiguration}
        handleClick={handleClick}
      />

      <ResetMapModal
        isSaving={isSaving}
        show={showReset}
        handleReset={handleReset}
        handleClose={handleCloseReset}
      />

      <SelectMapModal
        venueSlug={event?.venue?.slug}
        show={show}
        choice={choice}
        maps={seatmaps}
        templates={templates}
        selected={selectedId}
        setSelected={setSelectedId}
        initialSelected={initialSelectedId}
        isTemplate={choice === 2}
        isSaving={isSaving}
        handleClose={handleClose}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
      />

      <SaveModal
        show={showSave}
        step={saveStep}
        setStep={setSaveStep}
        name={templateName}
        setName={setTemplateName}
        isSaving={isSaving}
        handleClose={handleCloseSave}
        handleCancel={handleCloseSave}
        handleSubmit={handleSaveTemplate}
      />

      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        isSaving={isSaving || isReloading}
        onSave={handleSaveFromUnsavedModal}
        onContinueWithoutSaving={handleContinueWithoutSaving}
        onClose={handleCloseUnsavedChangesModal}
      />

      <SeatmapTutorial
        isOpen={showSeatmapTutorial}
        onClose={() => setShowSeatmapTutorial(false)}
        onComplete={markSeatmapTutorialComplete}
      />
    </>
  )
}
